import React, { lazy, Suspense } from "react";
import PropTypes from "prop-types";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";

import { classNames } from "@/helpers/classNames";

const ReactHlsPlayer = lazy(() => import("@/components/reactHlsPlayer.tsx"));

export const query = graphql`
  fragment ShopifyMediaSubquery on ShopifyMedia {
    mediaContentType
    alt
    preview {
      image {
        altText
        gatsbyImageData
        src
      }
    }
    ... on ShopifyExternalVideo {
      host
      embeddedUrl
    }
    ... on ShopifyMediaImage {
      image {
        gatsbyImageData
        src
        altText
      }
    }
    ... on ShopifyModel3d {
      filename
      sources {
        url
        format
        mimeType
      }
    }
    ... on ShopifyVideo {
      filename
      sources {
        url
        height
        width
      }
    }
  }

  fragment StrapiMediaSubquery on StrapiUploadFile {
    alternativeText
    file {
      url
      publicHLSVideoURL
      publicHLSThumbnailURL
      childImageSharp {
        gatsbyImageData
      }
    }
  }
`;

const imageExtensions = ["png", "jpg", "jepg", "svg", "avif", "webp"];

function MediaWrapper({
  media,
  alt,
  className = "",
  divClassName = "",
  videoClassName = null,
  style = {},
  divStyle = {},
  objectFit = "cover",
  objectPosition = "50% 50%",
  width = "100%",
  height = "100%",
  as = "div",
  src,
  ...other
}) {
  // eslint-disable-next-line no-unused-vars
  src = undefined;

  if (media) {
    const newAlt =
      alt || media.alternativeText || media.alt || media.altText || "";
    const gatsbyImageData =
      media.file?.childImageSharp?.gatsbyImageData ||
      media.image?.gatsbyImageData ||
      media.preview?.image?.gatsbyImageData ||
      media.gatsbyImageData ||
      null;
    const url =
      media.file?.url ||
      media.image?.src ||
      media.preview?.image?.src ||
      media.src ||
      null;

    if (gatsbyImageData) {
      return (
        <GatsbyImage
          image={gatsbyImageData}
          alt={newAlt}
          className={divClassName}
          imgClassName={className}
          style={divStyle}
          imgStyle={style}
          objectFit={objectFit}
          objectPosition={objectPosition}
          as={as}
          {...other}
        />
      );
    } else if (url && !imageExtensions.includes(url?.split(".").pop())) {
      const videoOriginal = url;
      const videoHls = media?.file?.publicHLSVideoURL;
      const thumbnail = media?.file?.publicHLSThumbnailURL;
      const newVideoClassname = videoClassName
        ? videoClassName
        : `${divClassName} ${className}`;

      return (
        <Suspense fallback={null}>
          <ReactHlsPlayer
            srcHls={videoHls}
            srcFallback={videoOriginal}
            width={width}
            height={height}
            className={newVideoClassname}
            style={{
              objectFit: objectFit,
              objectPosition: objectPosition,
              ...Object.assign(divStyle, style),
            }}
            poster={thumbnail}
            playsInline
            {...other}
          />
        </Suspense>
      );
    } else {
      return React.createElement(
        as,
        {
          className: classNames("overflow-hidden", divClassName),
          style: divStyle,
        },
        <img
          src={url}
          alt={newAlt}
          className={classNames("m-auto", className)}
          style={{
            height: height,
            width: width,
            objectFit: objectFit,
            objectPosition: objectPosition,
            ...style,
          }}
          {...other}
        />
      );
    }
  } else {
    return React.createElement(as, {
      className: classNames("overflow-hidden", divClassName),
      style: divStyle,
    });
  }
}

MediaWrapper.propTypes = {
  media: PropTypes.object,
  alt: PropTypes.string,
  className: PropTypes.string,
  divClassName: PropTypes.string,
  videoClassName: PropTypes.string,
  style: PropTypes.object,
  divStyle: PropTypes.object,
  objectFit: PropTypes.string,
  objectPosition: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  as: PropTypes.elementType,
  src: PropTypes.object,
};

export default MediaWrapper;
